import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';

// Folded until someone opens it; their choice is remembered per browser
// (storage can be unavailable, e.g. private mode: then it starts folded)
const readHidden = (key) => {
  try {
    return localStorage.getItem(key) !== 'false';
  } catch {
    return true;
  }
};
const writeHidden = (key, hidden) => {
  try {
    localStorage.setItem(key, String(hidden));
  } catch {
    // Not remembered; it still hides for now
  }
};

// Extra detail for a step, closed until asked for
const MoreToggle = ({ showLabel, hideLabel, content }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="seg-use__more">
      <button
        type="button"
        className="seg-use__more-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <ChevronDownIcon
          size={18}
          className={open ? 'seg-use__more-chevron--open' : ''}
        />
        {open ? hideLabel : showLabel}
      </button>
      {open && <div className="seg-use__more-body">{content}</div>}
    </div>
  );
};

MoreToggle.propTypes = {
  showLabel: PropTypes.node.isRequired,
  hideLabel: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
};

/**
 * An introduction at the top of a list page (Segments, User tiers): a
 * collapsible card with a title and subtitle, example uses side by side with
 * an arrow between them (each with a preview, e.g. the Messages or
 * Spotlight phone, and an optional audience chip), and a small row on where
 * the data comes from. It starts folded; opening or folding it is
 * remembered per browser (`storageKey`).
 * Styles: .seg-showcase / .seg-use in form-card.scss.
 */
const FeatureShowcase = ({
  storageKey,
  icon: Icon,
  title,
  subtitle,
  showLabel,
  uses,
  sourcesIcon: SourcesIcon,
  sources,
  collapsible,
  action,
}) => {
  const [storedHidden, setHidden] = useState(() => readHidden(storageKey));
  // Always open where it can't be folded (the Learn more page)
  const hidden = collapsible && storedHidden;
  const toggle = () => {
    setHidden(!hidden);
    writeHidden(storageKey, !hidden);
  };
  const headContent = (
    <>
      <span className="seg-showcase__head-icon">
        <Icon size={18} />
      </span>
      <span className="seg-showcase__head-text">
        <strong>{title}</strong>
        <span>{hidden ? showLabel : subtitle}</span>
      </span>
    </>
  );

  return (
    <section
      className={`seg-showcase mt-3 mb-3${hidden ? ' seg-showcase--hidden' : ''}`}
    >
      {collapsible ? (
        <button
          type="button"
          className="seg-showcase__head"
          onClick={toggle}
          aria-expanded={!hidden}
        >
          {headContent}
          <ChevronDownIcon size={20} className="seg-showcase__chevron" />
        </button>
      ) : (
        <div className="seg-showcase__head seg-showcase__head--static">
          {headContent}
          {action}
        </div>
      )}

      {!hidden && (
        <div className="seg-showcase__body">
          <div className="seg-showcase__uses">
            {uses.map((use, index) => {
              const UseIcon = use.icon;
              return (
                <Fragment key={use.key}>
                  {index > 0 && (
                    <span className="seg-showcase__arrow" aria-hidden>
                      <ArrowRightIcon size={22} />
                    </span>
                  )}
                  <div className="seg-use">
                    {use.step && (
                      <span className="seg-use__step">{use.step}</span>
                    )}
                    <div className="seg-use__text">
                      <span className="seg-use__icon">
                        <UseIcon size={18} />
                      </span>
                      <span>
                        <strong>{use.label}</strong> {use.text}
                      </span>
                    </div>
                    {(use.preview || use.items?.length > 0) && (
                      <div
                        className={`seg-use__media${
                          use.itemsBeside ? ' seg-use__media--beside' : ''
                        }`}
                      >
                        {use.preview && (
                          <div
                            className="seg-use__phone"
                            aria-hidden={!use.interactive}
                          >
                            {use.preview}
                            {use.chip && (
                              <span className="seg-use__audience">
                                <AccountMultipleIcon size={12} />
                                {use.chip}
                              </span>
                            )}
                          </div>
                        )}
                        {use.items?.length > 0 && (
                          <ul className="seg-use__items">
                            {use.items.map((item) => {
                              const ItemIcon = item.icon;
                              return (
                                <li key={item.key}>
                                  <ItemIcon size={16} />
                                  {item.label}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                    {use.cards?.length > 0 && (
                      <div className="seg-use__cards">
                        {use.cards.map((card) => {
                          const CardIcon = card.icon;
                          // A few short badges go on the right; a longer
                          // list goes under the text, where it can wrap
                          const badgesBelow = card.badges?.length > 3;
                          const badges = card.badges?.length > 0 && (
                            <span
                              className={`seg-mini__badges${
                                badgesBelow ? ' seg-mini__badges--below' : ''
                              }`}
                            >
                              {card.badges.map((badge) => (
                                <span key={badge}>{badge}</span>
                              ))}
                            </span>
                          );
                          return (
                            <div className="seg-mini" key={card.key}>
                              <span className="seg-mini__icon">
                                <CardIcon size={18} />
                              </span>
                              <span className="seg-mini__body">
                                <strong>{card.title}</strong>
                                <span>{card.text}</span>
                                {card.points?.length > 0 && (
                                  <ul className="seg-mini__points">
                                    {card.points.map((point) => (
                                      <li key={point.label}>
                                        <b>{point.label}</b> {point.text}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {badgesBelow && badges}
                              </span>
                              {!badgesBelow && badges}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {use.more && <MoreToggle {...use.more} />}
                    {use.points?.length > 0 && (
                      <ul className="seg-use__points">
                        {use.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>

          {sources && (
            <div className="seg-showcase__sources">
              <span className="seg-showcase__sources-icon">
                <SourcesIcon size={16} />
              </span>
              {sources}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

FeatureShowcase.propTypes = {
  storageKey: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node.isRequired,
  showLabel: PropTypes.node.isRequired,
  uses: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.node.isRequired,
      text: PropTypes.node.isRequired,
      // Optional: "Step 1", bullet points, an example, a list with icons
      step: PropTypes.node,
      points: PropTypes.arrayOf(PropTypes.string),
      preview: PropTypes.node,
      // The preview can be clicked (so it stays available to screen readers)
      interactive: PropTypes.bool,
      // The items as a column beside the preview instead of under it
      itemsBeside: PropTypes.bool,
      // Extra detail, closed until asked for: { showLabel, hideLabel, content }
      more: PropTypes.shape({
        showLabel: PropTypes.node,
        hideLabel: PropTypes.node,
        content: PropTypes.node,
      }),
      // Small cards explaining the parts of the step
      cards: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          icon: PropTypes.elementType.isRequired,
          title: PropTypes.node.isRequired,
          text: PropTypes.node.isRequired,
          badges: PropTypes.arrayOf(PropTypes.string),
          // Labelled points under the text: [{ label, text }]
          points: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.node.isRequired,
              text: PropTypes.node.isRequired,
            })
          ),
        })
      ),
      chip: PropTypes.node,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          icon: PropTypes.elementType.isRequired,
          label: PropTypes.node.isRequired,
        })
      ),
    })
  ).isRequired,
  sourcesIcon: PropTypes.elementType,
  sources: PropTypes.node,
  // false: always open, with `action` (e.g. a link to the page) in the head
  collapsible: PropTypes.bool,
  action: PropTypes.node,
};

FeatureShowcase.defaultProps = {
  sourcesIcon: null,
  sources: null,
  collapsible: true,
  action: null,
};

export default FeatureShowcase;
